import { ArrowClockwiseIcon, XIcon } from '@phosphor-icons/react'
import { useMemo, useState } from 'react'
import { Button } from 'app/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'app/components/ui/select'
import { fieldLabelByKey } from 'app/lib/workflows/dataContext'
import { filterPhrase, formatEventTimestamp } from 'app/lib/workflows/format'
import { applyChips, groupByPerson, type LookbackChip } from 'app/lib/workflows/lookback/lookbackRows'
import { PAGE_SIZE } from 'app/lib/workflows/lookback/loadLookback'
import type { MatchWindowDays } from 'app/lib/workflows/lookback/types'
import { cn } from 'app/lib/utils'
import type { TriggerLookback } from './useTriggerLookback'

export type MatchesPanelProps = {
  open: boolean
  onClose: () => void
  lookback: TriggerLookback
  /** What the trigger listens to, in lower case: "contact", "appointment", "form". */
  sourceNoun: string
  timeZone?: string | null
  /** No location chosen yet, so there is nothing to ask. */
  needsLocation?: boolean
  /** A date trigger: rows are people whose date came due, not events. */
  dateTrigger?: boolean
} & (
  | {
      /** Who a date trigger reaches next. */
      mode: `upcoming`
      windowDays: MatchWindowDays
      onWindowChange: (days: MatchWindowDays) => void
    }
  | {
      /** The past events turning the workflow on would replay, since the backfill date. */
      mode: `backfill`
      since: string
    }
)

const WINDOWS = [
  { days: 7, label: `7 days` },
  { days: 30, label: `30 days` },
  { days: 90, label: `90 days` },
] as const

const CHIPS = [
  { chip: `notEnrolled`, label: `Not already enrolled` },
  { chip: `hideClosed`, label: `Hide completed and cancelled` },
] as const

const NOTE = `rounded-md px-2 py-1.5 text-xs leading-relaxed`

const formatDay = (iso: string, timeZone?: string) =>
  new Date(iso).toLocaleDateString(`en-US`, { dateStyle: `medium`, timeZone })

/** Read-only preview: who a date trigger reaches next, or what going live would replay. */
export function MatchesPanel(props: MatchesPanelProps) {
  const { open, onClose, lookback, sourceNoun, timeZone, needsLocation, dateTrigger } = props
  const [chips, setChips] = useState<Set<LookbackChip>>(new Set())
  const visible = useMemo(() => applyChips(lookback.rows, chips), [lookback.rows, chips])
  const groups = useMemo(() => groupByPerson(visible), [visible])

  if (!open) return null

  const answered = !!lookback.loadedAt
  const upcoming = props.mode === `upcoming`
  // "in the next 30 days" or "since Oct 5, 2026"
  const span = upcoming
    ? `in the next ${WINDOWS.find((w) => w.days === props.windowDays)?.label}`
    : // The day was picked on this browser's clock (TriggerPanel), so it reads back on the same one.
      `since ${formatDay(props.since)}`
  // Completed and cancelled describe appointments; a contact date has no status to hide.
  const chipChoices = sourceNoun === `appointment` ? CHIPS : CHIPS.filter(({ chip }) => chip !== `hideClosed`)

  const toggleChip = (chip: LookbackChip) =>
    setChips((previous) => {
      const next = new Set(previous)
      if (!next.delete(chip)) next.add(chip)
      return next
    })

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* The backdrop, identical to RunsPanel's: the design-system lint refuses a restyled Button
          here, and the two slide-overs must close the same way. */}
      <button type="button" aria-label="Close matches" className="flex-1 bg-overlay/20" onClick={onClose} />
      <div className="flex w-140 max-w-full flex-col border-l border-default bg-surface shadow-xl">
        <div className="flex items-center justify-between gap-2 border-b border-default px-4 py-3">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-strong">{upcoming ? `Matches` : `Backfill`}</h2>
            <p className="text-xs text-subtle">
              {/* A count is only a claim once a load has actually answered. */}
              {lookback.loadedAt
                ? `${lookback.total.toLocaleString()} ${span}`
                : needsLocation
                  ? `No location chosen`
                  : lookback.status === `error`
                    ? `Couldn't load`
                    : `Loading…`}
            </p>
          </div>
          <div className="flex items-center gap-1">
            {upcoming && (
              <Select
                value={String(props.windowDays)}
                onValueChange={(value) => props.onWindowChange(Number(value) as MatchWindowDays)}
              >
                <SelectTrigger className="h-8 w-30 text-xs" aria-label="Look ahead">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WINDOWS.map((window) => (
                    <SelectItem key={window.days} value={String(window.days)}>
                      Next {window.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button variant="white" size="icon-sm" title="Refresh" onClick={lookback.refresh}>
              <ArrowClockwiseIcon size={16} weight="bold" />
            </Button>
            <Button variant="white" size="icon-sm" title="Close" onClick={onClose}>
              <XIcon size={16} weight="bold" />
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="space-y-2 border-b border-default bg-surface-subtle p-4">
            <p className="text-xs leading-relaxed text-subtle">
              {upcoming
                ? `The workflow reaches these people on its own, soonest first. Nothing is sent from here.`
                : `Turning the workflow on also runs it for these ${dateTrigger ? `people` : `events`}, newest first. Checks on the live record, re-enrollment and contact matching can still skip some.`}
            </p>
            {!upcoming &&
              lookback.eventHistoryStart &&
              Date.parse(props.since) < Date.parse(lookback.eventHistoryStart) && (
                <p className={cn(NOTE, `bg-surface-muted text-subtle`)}>
                  Event history starts {formatDay(lookback.eventHistoryStart, `UTC`)}. Earlier matches are rebuilt from
                  records and marked.
                </p>
              )}
            {/* An error must never read as "0 matches": the last good list stays, labelled as old. */}
            {lookback.status === `error` && (
              <p className={cn(NOTE, `bg-danger-subtle text-danger-strong`)}>
                Couldn&rsquo;t refresh the matches.
                {lookback.loadedAt
                  ? ` Showing the result from ${formatEventTimestamp(lookback.loadedAt.toISOString(), false, timeZone)}.`
                  : ``}
              </p>
            )}
            <div className="flex flex-wrap gap-1.5">
              {chipChoices.map(({ chip, label }) => (
                <Button
                  key={chip}
                  variant={chips.has(chip) ? `primary` : `white`}
                  size="sm"
                  aria-pressed={chips.has(chip)}
                  onClick={() => toggleChip(chip)}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>

          {needsLocation && (
            <p className="p-6 text-xs text-subtle">Pick this workflow&rsquo;s location to see who it reaches.</p>
          )}
          {answered && lookback.rows.length === 0 && lookback.status !== `error` && (
            <div className="space-y-1 p-6 text-xs leading-relaxed text-subtle">
              {/* A near miss names the condition that turned people away. */}
              <p>
                {lookback.considered === 0
                  ? upcoming
                    ? `Nobody comes due ${span}.`
                    : dateTrigger
                      ? `Nobody came due ${span}.`
                      : `No ${sourceNoun} events were recorded at this workflow's location ${span}.`
                  : `${lookback.considered} ${upcoming ? `${sourceNoun}s come due` : dateTrigger ? `${sourceNoun}s came due` : `${sourceNoun} events were recorded`} ${span}.${lookback.rejections.length > 0 ? ` None passed:` : ` None matched.`}`}
              </p>
              {lookback.rejections.map(({ filter, count }, index) => (
                <p key={index}>
                  <span className="text-default">{filterPhrase(filter)}</span> turned away {count}.
                </p>
              ))}
            </div>
          )}
          {lookback.rows.length > 0 && visible.length === 0 && (
            <p className="p-6 text-center text-xs text-subtle">
              Nothing matches these filters. Clear a filter to see all matches.
            </p>
          )}

          <ul className="divide-y divide-subtle">
            {groups.map((group) => (
              <li key={group.key} className="px-4 py-2.5">
                <p className="text-xs font-medium text-strong">{group.name}</p>
                <ul className="mt-1.5 space-y-2">
                  {group.rows.map((row) => {
                    // A contact date is a calendar day: its midnight is not a time anyone chose.
                    const when =
                      (upcoming || dateTrigger) && row.match.sourceType === `Contact`
                        ? formatDay(row.match.occurredAt, timeZone ?? undefined)
                        : formatEventTimestamp(row.match.occurredAt, false, timeZone)
                    // An appointment's date is a calendar day with its clock time held separately;
                    // rendered as a timestamp it shows a midnight nobody booked.
                    const appointmentDay = row.source?.date
                      ? `, ${formatDay(row.source.date, `UTC`)}${row.source.time ? ` at ${row.source.time}` : ``}`
                      : ``
                    return (
                      <li key={row.match.id} className="space-y-0.5">
                        <p className="text-xs text-default">
                          {upcoming ? `Reaches them ${when}` : dateTrigger ? `Came due ${when}` : when}
                          {row.match.basis === `record` ? <span className="text-minimal"> · from record</span> : null}
                          {Object.entries(row.match.matchedValues).map(([attribute, value]) => (
                            <span key={attribute} className="text-subtle">
                              {` · ${fieldLabelByKey(attribute) ?? attribute}: `}
                              <span className="font-mono text-caption-sm text-default">
                                {Array.isArray(value) ? value.join(` `) : String(value ?? ``)}
                              </span>
                            </span>
                          ))}
                        </p>
                        {/* Only an appointment has a state of its own worth showing beside the date. */}
                        {row.source && (
                          <p className="text-xs text-subtle">
                            {`Appointment now: ${row.source.type ? `${row.source.type}, ` : ``}${row.source.status ?? `no status`}${appointmentDay}`}
                          </p>
                        )}
                        {row.inTargetPipeline && <p className="text-xs text-subtle">Already in the target pipeline</p>}
                        {row.blockedReason && <p className="text-xs text-minimal">{row.blockedReason}</p>}
                      </li>
                    )
                  })}
                </ul>
              </li>
            ))}
          </ul>
          {lookback.hasMore && (
            <div className="flex items-center justify-between gap-2 border-t border-default px-4 py-3">
              {/* Filters only see what is loaded, so say how much that is. */}
              <p className="text-xs text-subtle">
                Showing the {upcoming ? `first` : `newest`} {lookback.rows.length.toLocaleString()} of{` `}
                {lookback.total.toLocaleString()}
              </p>
              <Button
                variant="white"
                size="sm"
                disabled={lookback.status === `loading` || lookback.status === `loadingMore`}
                onClick={lookback.loadMore}
              >
                {lookback.status === `loadingMore`
                  ? `Loading…`
                  : `Load ${Math.min(PAGE_SIZE, lookback.total - lookback.rows.length)} more`}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
